import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { useClientSettings } from "../lib/ClientSettingsContext";

const PRODUCTION_MODELS: { value: string; label: string; description: string }[] = [
  { value: "v4", label: "V4 — docTR CRNN + Binary Gate (Sibit fine-tune)",
    description: "Schnellster (~80ms/cell), Apache-2.0. Eimer: 86% TP, 1 silent. Default." },
  { value: "glm", label: "GLM-OCR 0.9B (Zhipu, MIT)",
    description: "VLM, ~2s/cell warm, MIT-Lizenz." },
  { value: "paddle", label: "PaddleOCR-VL 1.5 (Apache-2.0)",
    description: "VLM, ~1.5s/cell warm, OpenVINO-beschleunigt." },
  { value: "qwen", label: "Qwen2-VL-2B-Instruct (Apache-2.0)",
    description: "Kleines VLM, ~1.5s/cell warm." },
  { value: "qwen3", label: "Qwen3-VL-8B-Instruct (Apache-2.0)",
    description: "Größtes VLM, ~3s/cell warm mit V4-Gate-Hybrid. Beste Quality wenn warm. Aktueller Default." },
  { value: "ppocrv6", label: "PP-OCRv6 medium (PaddleOCR, Apache-2.0)",
    description: "Klassische Detektion+Erkennung (kein VLM, halluziniert nicht), ~30× schneller als qwen3. Stark auf getippt; Handschrift gemischt. Ungültige Symbole werden auf gültige Klasse oder leer normalisiert." },
  { value: "tesseract", label: "Tesseract 5.x (Apache-2.0)",
    description: "Klassisch, ~50ms/cell. Stark auf gedruckt, schwach auf Handschrift." },
];

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function SettingsAdmin() {
  const { selectedClientId: clientId } = useClientContext();
  const { settings, setSettingRemote } = useClientSettings();
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [rules, setRules] = useState<Awaited<ReturnType<typeof api.admin.rulesPreview>> | null>(null);
  const [savingModel, setSavingModel] = useState(false);

  const load = async () => {
    try {
      const r = await api.admin.monthSummary(month, clientId);
      setSummary(r);
    } catch (e: any) {
      setMsg(`Fehler: ${e.message}`);
    }
  };

  const loadRules = async () => {
    try {
      const r = await api.admin.rulesPreview(3, clientId);
      setRules(r);
    } catch (e: any) {
      setMsg(`Fehler beim Laden der Regeln: ${e.message}`);
    }
  };

  useEffect(() => {
    load();
    loadRules();
  }, [month, clientId]);

  const reOcr = async () => {
    if (!confirm("Alle fehlgeschlagenen OCR-Karten erneut verarbeiten?")) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.admin.reOcr(clientId, month);
      setMsg(`✓ ${r.requeued_timesheets + r.requeued_expenses} Karten erneut in Queue.`);
      await load();
    } catch (e: any) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const rebuildRules = async () => {
    if (!confirm("Regel-Datei aus Korrekturen neu erstellen? Worker liest die Aggregate-View und schreibt rules.jsonl.")) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.admin.rulesRebuild();
      setMsg(r.enqueued ? `✓ Regel-Rebuild eingeplant (Job ${r.job_id ?? "?"}). Sidecar lädt nach.` : "Rebuild abgelehnt.");
      // Reload preview after a short delay to capture worker progress
      setTimeout(loadRules, 5000);
    } catch (e: any) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onModelChange = async (newModel: string) => {
    if (newModel === settings.production_ocr_model) return;
    setSavingModel(true);
    setMsg(null);
    try {
      await setSettingRemote("ocr.production_model", newModel);
      setMsg(`✓ Production-OCR-Modell auf "${newModel}" umgestellt. Nächster Upload nutzt dieses Modell.`);
    } catch (e: any) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setSavingModel(false);
    }
  };

  const activeModel = PRODUCTION_MODELS.find(m => m.value === settings.production_ocr_model);

  return (
    <div>
      <div className="toolbar">
        <label className="field-inline">
          <span>Monat</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <span className="muted">Mandant: {clientId}</span>
        {msg && <span>{msg}</span>}
      </div>

      {/* Production OCR Model selector */}
      <div className="kv-card" style={{ marginTop: 14 }}>
        <div className="kv-header">
          <strong>Production OCR-Modell</strong>
        </div>
        <p style={{ fontSize: 13, color: "#555", margin: "8px 0", lineHeight: 1.5 }}>
          Dieses Modell wird verwendet wenn eine Karte über /board hochgeladen wird.
          Änderung wirkt sofort beim nächsten Upload — kein Container-Restart nötig.
          Aktuell: <strong>{settings.production_ocr_model}</strong>
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <select value={settings.production_ocr_model}
                  onChange={(e) => onModelChange(e.target.value)}
                  disabled={savingModel}
                  style={{ padding: "6px 10px", fontSize: 14, minWidth: 320 }}>
            {PRODUCTION_MODELS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {savingModel && <span style={{ fontSize: 12, color: "#555" }}>Speichere…</span>}
        </div>
        {activeModel && (
          <p style={{ fontSize: 12, color: "#777", marginTop: 8 }}>
            {activeModel.description}
          </p>
        )}
        <p style={{ fontSize: 11, color: "#999", marginTop: 6 }}>
          Zum Vergleich mehrerer Modelle Side-by-Side: Settings → OCR-Test
        </p>
      </div>

      {summary && (
        <div className="kv-card">
          <div className="kv-header">
            <strong>Monats-Zusammenfassung</strong>
          </div>
          <dl className="kv-grid">
            <dt>Stundenlisten pro Status</dt>
            <dd>
              {Object.entries(summary.timesheets).length === 0
                ? "—"
                : Object.entries(summary.timesheets).map(([state, n]) => (
                    <span key={state} style={{ marginRight: 10 }}>
                      {state}: <strong>{n as number}</strong>
                    </span>
                  ))}
            </dd>
            <dt>Diäten pro Kind + Status</dt>
            <dd>
              {summary.expenses.length === 0
                ? "—"
                : summary.expenses.map((e: any, i: number) => (
                    <span key={i} style={{ marginRight: 10 }}>
                      {e.kind}/{e.state}: <strong>{e.count}</strong>
                    </span>
                  ))}
            </dd>
            <dt>OCR-Fehler</dt>
            <dd>
              {summary.ocr_failed_count > 0 ? (
                <span style={{ color: "#dc2626" }}>
                  {summary.ocr_failed_count} Karten mit <code>data.ocr_error</code>
                </span>
              ) : (
                <span className="muted">keine</span>
              )}
            </dd>
            <dt>Feedback-Korrekturen</dt>
            <dd>{summary.feedback_count} Einträge im Monat</dd>
          </dl>
        </div>
      )}

      <div className="kv-card">
        <div className="kv-header">
          <strong>Aktionen</strong>
        </div>
        <div className="admin-actions">
          <button
            className="primary"
            onClick={reOcr}
            disabled={busy || !summary || summary.ocr_failed_count === 0}
            title={
              summary?.ocr_failed_count === 0
                ? "Keine fehlgeschlagenen Karten"
                : "Emit neue timesheet.created / expense.created Events"
            }
          >
            Fehlgeschlagene OCR neu starten
          </button>
          <a
            href={api.admin.feedbackCsvUrl(month, clientId)}
            className="upload-button"
            style={{ textDecoration: "none" }}
          >
            📄 Feedback als CSV herunterladen
          </a>
        </div>
        <p className="muted small">
          Re-OCR: clearet <code>data.ocr_error</code> und triggert den Worker
          neu (setzt voraus dass die OCR-Engine inzwischen läuft).
          Feedback-CSV: alle Field-Level-Korrekturen des Monats, für externe Analyse.
        </p>
      </div>

      <div className="kv-card">
        <div className="kv-header">
          <strong>Lern-Regeln (Closed-Loop)</strong>
          <button className="primary" onClick={rebuildRules} disabled={busy} style={{ marginLeft: "auto" }}>
            Regeln neu erstellen
          </button>
        </div>
        {!rules ? (
          <p className="muted">Lade…</p>
        ) : rules.total === 0 ? (
          <p className="muted">Noch keine Korrektur-Muster gefunden. Sobald Birgit Zellen
            korrigiert, erscheinen die häufigsten hier — ab Häufigkeit ≥ {rules.threshold} werden
            sie automatisch als Regel aktiv.</p>
        ) : (
          <>
            <p className="muted small">
              {rules.total} Muster aus Korrekturen. Promotable (≥ {rules.threshold}× gleich)
              werden bei nächstem „Regeln neu erstellen" zur <code>rules.jsonl</code> hinzugefügt.
            </p>
            <table className="ts-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Feld</th>
                  <th>Falsch</th>
                  <th>Richtig</th>
                  <th style={{ textAlign: "right" }}>Anzahl</th>
                  <th style={{ textAlign: "right" }}>Karten</th>
                  <th>Aktiv</th>
                </tr>
              </thead>
              <tbody>
                {rules.patterns.slice(0, 50).map((p, i) => (
                  <tr key={i}>
                    <td><code>{p.field_path}</code></td>
                    <td><code>{p.old_value}</code></td>
                    <td><strong>{p.new_value}</strong></td>
                    <td style={{ textAlign: "right" }}>{p.occurrence_count}</td>
                    <td style={{ textAlign: "right" }}>{p.distinct_cards}</td>
                    <td>{p.promotable ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <p className="muted small">
          Funktionsweise: Birgit korrigiert eine Zelle → wir merken uns das Paar (alt → neu).
          Tritt die gleiche Korrektur ≥ {rules?.threshold ?? 3}× auf, wird sie zur Regel und der
          OCR-Sidecar wendet sie automatisch beim nächsten Lauf an. Closed-Loop, ohne erneutes Training.
        </p>
      </div>
    </div>
  );
}
